import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { sendChatMessage } from '@/lib/api';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Wizbang's AI assistant. How can I help you today? Ask me about our services, book an appointment, or get any information you need.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await sendChatMessage({
        message: userMessage,
        session_id: sessionId,
      });
      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.data.response },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm sorry, I'm having trouble connecting right now. Please try again or contact us at sayhi@wizbang.in",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chat-open-btn"
          className="w-14 h-14 rounded-full bg-[#ff5a1f] text-white flex items-center justify-center shadow-lg hover:bg-[#e04e1a] transition-colors glow-orange btn-press"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="w-[380px] h-[520px] bg-[#0A0A0A] border border-[#1F1F1F] rounded-sm shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-[#ff5a1f] px-4 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold">Wizbang AI</h3>
              <p className="text-white/80 text-xs">We typically reply instantly</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chat-close-btn"
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-sm text-sm ${
                    msg.role === 'user'
                      ? 'bg-[#ff5a1f] text-white'
                      : 'bg-[#121212] text-[#EDEDED] border border-[#1F1F1F]'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#121212] border border-[#1F1F1F] px-4 py-3 rounded-sm">
                  <Loader2 className="w-5 h-5 text-[#ff5a1f] animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[#1F1F1F]">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                data-testid="chat-input"
                className="flex-1 bg-[#121212] border border-[#333] rounded-sm px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#ff5a1f] transition-colors"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                data-testid="chat-send-btn"
                className="bg-[#ff5a1f] text-white p-3 rounded-sm hover:bg-[#e04e1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-press"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
